import { useEffect, useRef, useState } from 'react';
import { REACTIONS, type PublicStream, type ReactionId } from '@zip2live/shared';
import { ThumbDownIcon, ThumbUpIcon } from './Icons.tsx';
import { count } from './StreamStats.tsx';
import { onReactions, react, savedVote, saveVote } from '../lib/reactions.ts';
import { api } from '../lib/api.ts';

const EMOJI = new Map<string, string>(REACTIONS.map((r) => [r.id, r.emoji]));
const MAX_FLOATING = 40;

/** Emoji floating up over the video as viewers react. Decorative: screen readers skip it. */
export function FloatingReactions({ streamId }: { streamId: string }) {
  const [items, setItems] = useState<{ key: number; emoji: string; right: number; drift: number; delay: number }[]>([]);
  const next = useRef(0);
  useEffect(
    () =>
      onReactions(streamId, (counts) => {
        const added = (Object.entries(counts) as [ReactionId, number][]).flatMap(([id, n]) =>
          Array.from({ length: Math.min(n, 8) }, (_, i) => ({
            key: next.current++,
            emoji: EMOJI.get(id) ?? '❤️',
            right: 4 + Math.random() * 14,
            drift: Math.round((Math.random() - 0.5) * 60),
            delay: i * 120,
          })),
        );
        setItems((cur) => [...cur, ...added].slice(-MAX_FLOATING));
      }),
    [streamId],
  );
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
      {items.map((it) => (
        <span
          key={it.key}
          className="float-reaction absolute bottom-16 text-3xl sm:text-4xl"
          style={{ right: `${it.right}%`, animationDelay: `${it.delay}ms`, ['--drift' as string]: `${it.drift}px` }}
          onAnimationEnd={() => setItems((cur) => cur.filter((x) => x.key !== it.key))}
        >
          {it.emoji}
        </span>
      ))}
    </div>
  );
}

/** One tap = one reaction, floating up for everyone watching. Live streams only. */
export function ReactionButtons({ streamId, vertical = false, className = '' }: { streamId: string; vertical?: boolean; className?: string }) {
  const list = vertical ? REACTIONS.slice(0, 3) : REACTIONS;
  return (
    <div className={`flex ${vertical ? 'flex-col' : 'flex-wrap'} gap-1.5 ${className}`} role="group" aria-label="React">
      {list.map((r) => (
        <button
          key={r.id}
          type="button"
          aria-label={`React ${r.id}`}
          onClick={() => react(streamId, r.id)}
          className={`inline-flex items-center justify-center rounded-full text-xl transition active:scale-125 ${vertical ? 'h-12 w-12 bg-black/45' : 'h-10 w-10 bg-white/10 hover:bg-white/20'}`}
        >
          {r.emoji}
        </button>
      ))}
    </div>
  );
}

/**
 * Thumbs up and down. Viewers see the like count; the creator (whose view carries dislikes) sees
 * both counts and can't vote on their own stream.
 */
export function VoteButtons({ stream, compact = false }: { stream: PublicStream; compact?: boolean }) {
  const owner = stream.dislikes !== undefined;
  const [mine, setMine] = useState<1 | -1 | 0>(() => savedVote(stream.id));
  const [likes, setLikes] = useState(stream.likes);
  const [busy, setBusy] = useState(false);
  useEffect(() => setLikes(stream.likes), [stream.likes]);
  const pill = compact ? 'h-12 min-w-12 flex-col gap-0.5 rounded-full bg-black/45 px-2 text-[11px]' : 'h-10 gap-1.5 rounded-full bg-white/10 px-3.5 text-sm hover:bg-white/20';
  if (owner) {
    return (
      <span className={`inline-flex items-center font-bold text-white/85 ${pill}`} title="Likes and dislikes on your stream">
        <ThumbUpIcon /> {count(stream.likes)} <span className="mx-1 opacity-40" aria-hidden>·</span> <ThumbDownIcon /> {count(stream.dislikes ?? 0)}
      </span>
    );
  }
  const cast = async (value: 1 | -1) => {
    const next = mine === value ? 0 : value;
    const before = { mine, likes };
    setMine(next);
    setLikes((n) => n + (next === 1 ? 1 : 0) - (mine === 1 ? 1 : 0));
    setBusy(true);
    try {
      const res = await api.vote(stream.id, next);
      setMine(res.mine);
      setLikes(res.likes);
      saveVote(stream.id, res.mine);
    } catch {
      setMine(before.mine);
      setLikes(before.likes);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className={`flex ${compact ? 'flex-col' : ''} gap-1.5`}>
      <button type="button" disabled={busy} aria-pressed={mine === 1} aria-label={`Like, ${likes} likes`} onClick={() => void cast(1)} className={`inline-flex items-center justify-center font-bold text-white ${pill} ${mine === 1 ? '!bg-race' : ''}`}>
        <ThumbUpIcon filled={mine === 1} />
        <span className="tabular-nums">{count(likes)}</span>
      </button>
      <button type="button" disabled={busy} aria-pressed={mine === -1} aria-label="Dislike" onClick={() => void cast(-1)} className={`inline-flex items-center justify-center font-bold text-white ${pill} ${mine === -1 ? '!bg-white/30' : ''}`}>
        <ThumbDownIcon filled={mine === -1} />
      </button>
    </div>
  );
}
